/**
 * TanStack Query hooks for `affiliate-groups`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	assign,
	create,
	getOne,
	list,
	unassign,
	affiliateGroupsQueryKeys,
	type AffiliateGroupDetail,
	type AffiliateGroupRow,
	type AssignMembershipBody,
	type AssignResponse,
	type CreateAffiliateGroupBody,
	type ListAffiliateGroupsResponse,
	type UnassignResponse,
} from "./api";

export function useAffiliateGroupsList(): UseQueryResult<
	ListAffiliateGroupsResponse,
	Error
> {
	return useQuery({
		queryKey: affiliateGroupsQueryKeys.list(),
		queryFn: list,
	});
}

export function useAffiliateGroupDetail(
	id: string | null,
): UseQueryResult<AffiliateGroupDetail, Error> {
	return useQuery({
		queryKey: id
			? affiliateGroupsQueryKeys.detail(id)
			: [...affiliateGroupsQueryKeys.all, "detail-disabled"],
		queryFn: () => getOne(id as string),
		enabled: !!id,
	});
}

export function useAffiliateGroupsCreate(): UseMutationResult<
	AffiliateGroupRow,
	Error,
	CreateAffiliateGroupBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateGroupsQueryKeys.all });
		},
	});
}

export function useAffiliateGroupAssign(
	groupId: string,
): UseMutationResult<AssignResponse, Error, AssignMembershipBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body) => assign(groupId, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateGroupsQueryKeys.all });
		},
	});
}

export function useAffiliateGroupUnassign(
	groupId: string,
): UseMutationResult<UnassignResponse, Error, AssignMembershipBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body) => unassign(groupId, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateGroupsQueryKeys.all });
		},
	});
}
